import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { useProfile } from "../context/ProfileContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

function nowLocalISO() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function toLocal(iso) {
  const d = new Date(iso);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export default function DoseForm({ onSaved, record }) {
  const { t } = useI18n();
  const { activeProfileId } = useProfile();
  const [insulin, setInsulin] = useState(record?.insulin_name || "");
  const [units, setUnits] = useState(record?.units ?? "");
  const [dt, setDt] = useState(record ? toLocal(record.datetime) : nowLocalISO());
  const [purpose, setPurpose] = useState(record?.purpose || "meal_correction");
  const [notes, setNotes] = useState(record?.notes || "");
  const [saving, setSaving] = useState(false);

  async function save() {
    const u = parseFloat(String(units).replace(",", "."));
    if (Number.isNaN(u) || u <= 0) {
      toast.error("A dose deve ser superior a zero.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        insulin_name: insulin, units: u, datetime: new Date(dt).toISOString(),
        purpose, notes,
      };
      if (record) await api.patch(`/profiles/${activeProfileId}/doses/${record.id}`, body);
      else await api.post(`/profiles/${activeProfileId}/doses`, body);
      toast.success(record ? t("record_updated") : t("d_saved"));
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4" data-testid="dose-form">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-[#627D98]">{t("d_insulin")} ({t("optional")})</Label>
          <Input value={insulin} onChange={(e) => setInsulin(e.target.value)} placeholder="Ex: NovoRapid" data-testid="dose-insulin" />
        </div>
        <div>
          <Label className="text-xs text-[#627D98]">{t("d_units")}</Label>
          <Input inputMode="decimal" value={units} onChange={(e) => setUnits(e.target.value)} autoFocus data-testid="dose-units" />
        </div>
      </div>
      <div>
        <Label className="text-xs text-[#627D98]">{t("d_datetime")}</Label>
        <Input type="datetime-local" value={dt} onChange={(e) => setDt(e.target.value)} data-testid="dose-datetime" />
      </div>
      <div>
        <Label className="text-xs text-[#627D98]">{t("d_purpose")}</Label>
        <Select value={purpose} onValueChange={setPurpose}>
          <SelectTrigger data-testid="dose-purpose"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="basal">{t("purp_basal")}</SelectItem>
            <SelectItem value="meal_correction">{t("purp_meal")}</SelectItem>
            <SelectItem value="unspecified">{t("purp_unspec")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs text-[#627D98]">{t("d_notes")} ({t("optional")})</Label>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="dose-notes" />
      </div>
      <Button onClick={save} disabled={saving} className="w-full h-12 bg-[#1A73E8] hover:bg-[#1557B0] text-white font-semibold" data-testid="dose-save">
        {saving ? t("loading") : t("save")}
      </Button>
    </div>
  );
}
